import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslocoModule } from '@jsverse/transloco';
import { CompanyTreeService } from 'app/core/company/company-tree.service';
import { NestedCompanyTreeNode } from 'app/core/company/company-tree.types';
import { CompanyType } from 'app/core/company/company.types';
import { Subject } from 'rxjs';


@Component({
  selector: 'company-tree',
  standalone: true,
    imports: [CommonModule, MatSidenavModule, MatInputModule, MatSelectModule, MatTreeModule, MatIconModule, MatButtonModule, MatMenuModule, TranslocoModule],
  templateUrl: './company-tree.component.html',
  styleUrls: ['./company-tree.component.scss']
})

export class CompanyTreeComponent {

    @ViewChild('tree') tree;
    @Output() actionSelected = new EventEmitter<'view' | 'edit' | 'delete' | 'move' | 'create'>();
    @Output() companyChanged = new EventEmitter<void>();

    /**
     * Nested tree control component that we will be used to display the companies in a hierarchical manner.
     * The companies that the logged user can see depend on the company type where he pertains.
     * @see CompanyTreeService.shouldBeDisplayed()
     */
    public companyTree = new NestedTreeControl<NestedCompanyTreeNode>(node => node.children);

    /**
     * Typical angular mat tree datasource to store the company tree.
     */
    public dataSource = new MatTreeNestedDataSource<NestedCompanyTreeNode>();

    /**
     * Used extensively in all fuse examples, TODO understand what exactly does and if it is needed,
     * specially as we migrate to signals instead of observables to share data.
     * @private
     */
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Structure that holds the companies that meet the filter criteria.
     * @private
     */
    private filteredCompanies: Map<number, NestedCompanyTreeNode>;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private companyTreeService: CompanyTreeService
    ) {
        this.dataSource.data = [];

        // Subscribe to tree changes
        toObservable(this.companyTreeService.companyTree).subscribe((treeNodes: NestedCompanyTreeNode[]) => {
            this.dataSource.data = treeNodes;

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * This property is used in the template to display only nodes that have children.
     * @param _
     * @param node
     */
    hasChild = (_: number, node: NestedCompanyTreeNode) => !!node.children && node.children.length > 0;

    /**
     * This property is used in the template to decide whether to show or hide the actions menu (3 dots) on each node.
     * Only client companies can have actions, so the menu is only shown for those nodes.
     */
    get shouldShowActionsMenu(): boolean {
        const type = this.selectedCompany?.typeCompanyId;
        return type != null && [
            CompanyType.Client,
            CompanyType.Doorwifi,
            CompanyType.Manusa
        ].includes(type);
    }

    /**
     * Used in the template to change the color of the currently selected company.
     * @param node
     */
    isNodeActive(node: NestedCompanyTreeNode): boolean {
        return this.companyTreeService.selectedCompany()?.id === node?.id;
    }

    /**
     * When a user clicks a node in the tree, the company must be stored in the corresponding service as a signal
     * so that any other components can access it at any time.
     * @param node
     */
    onCompanyClick(node: NestedCompanyTreeNode) {
        this.companyTreeService.selectCompany(node);
        this.companyChanged.emit();

    }

    /**
     * Used in the template to hide or show the element depending on the filter.
     * The 'invisible' class is a class from Tailwind that sets the hidden property css.
     * @param node
     */
    getNodeVisibilityClass(node) {
        return this.nodeMatchesFilter(node) ? '' : 'invisible';
    }

    /**
     * Checks if the given node exists in the filtered companies structure.
     * If the node is found, it should be displayed on the tree, otherwise hidden.
     * However, if the filter is empty, it is assumed that all companies should be visible (no filter).
     * @param node
     */
    nodeMatchesFilter(node: NestedCompanyTreeNode) {
        const query = this.companyTreeService.searchFilter();

        if (query === '' || query === null || !this.filteredCompanies) {
            return true;
        }

        return this.filteredCompanies.has(node.id);
    }

    /**
     * Filters the companies that match the given text value.
     * Internally, a map by id key is used to decide in the template if the node should be displayed or not.
     * @param filter
     */
    filterCompanies(filter: string) {
        this.filteredCompanies = this.buildFilteredMapFromTree(this.dataSource.data, filter);
        this.companyTreeService.searchFilter.set(filter);
    }

    /**
     * Checks if a given node matches the given text string.
     * The match logic is just a typical substring of the company name, case-insensitive.
     * @param node The node of the tree representing a company. Example node name: 'ASMV2M'.
     * @param filter A text to check if matches the given node. Example: 'asm' would match because matches the name.
     */
    nameMatchesFilter(node: NestedCompanyTreeNode, filter: string) {
        return node.name?.toLowerCase().includes(filter.toLowerCase());
    }

    /**
     * Given a nested tree and a text value, builds a Map structure where the keys are the ids of the
     * nodes that match the filter criteria. Later, this structure for internal use can be used in the template
     * with a ngIf in each node to check if the node id exists in the map.
     * @param tree
     * @param filter
     */
    buildFilteredMapFromTree(tree: NestedCompanyTreeNode[], filter: string): Map<number, NestedCompanyTreeNode> {
        const map = new Map<number, NestedCompanyTreeNode>();

        this.tree.treeControl.collapseAll();

        const traverse = function(node: NestedCompanyTreeNode, parentMatched: boolean) {
            let childMatches = false;
            let nodeMatches = this.nameMatchesFilter(node, filter);

            if (nodeMatches || parentMatched) {
                map.set(node.id, node);

                if (node.children) {
                    for (const child of node.children) {
                        map.set(child.id, child);
                    }
                }
            }

            if (node.children) {
                for (const child of node.children) {
                    childMatches =  traverse(child, nodeMatches) || childMatches;
                }

                if (childMatches) {
                    map.set(node.id, node);

                    if (!this.tree.treeControl.isExpanded(node)) {
                        this.tree.treeControl.expand(node);
                    }
                }
            }

            return nodeMatches || childMatches;

        }.bind(this);

        traverse(tree[0], false);

        return map;
    }

    get selectedCompany(): NestedCompanyTreeNode | null {
        return this.companyTreeService.selectedCompany();
    }

    /**
     * Given a name, returns HTML to replace the company name in the template with a highlighted css class.
     * Useful in the template to replace the name on each node that meets the filter criteria.
     * @param name
     */
    public highlight(name: string) {
        const query = this.companyTreeService.searchFilter();

        if (query === '' || query === null) {
            return name;
        }

        return name?.replace(new RegExp(query, 'gi'), match => {
            return '<span class="bg-cyan-300">' + match + '</span>';
        });
    }
}
